/**
 * SSE Streaming for Real-time Email Notifications
 * Only notifies for NEW messages (signals), not snapshot.
 */

import { API_BASE_URL } from '@sisumail/config';
import { InboxStream } from '@sisumail/inbox';
import { api, createScopedClient } from './api';

const API_URL = import.meta.env.VITE_API_URL || API_BASE_URL;

import {
    decryptMessagePreview,
    encodeBase64Url,
    generatePairingKeys,
    recoverMailboxKey,
} from './crypto';
import { logger } from './logger';
import {
    type Account,
    addMessage,
    getActiveAccount,
    getDeviceFingerprint,
    type Message,
    markAccountKeyStatus,
    setState,
    updateMessages,
} from './storage';

let inboxStream: InboxStream | null = null;

export type StreamEventHandler = {
    onSnapshot?: (messages: Message[]) => void;
    onSignal?: (message: Message) => void;
    onConnectionChange?: (status: 'connecting' | 'connected' | 'disconnected') => void;
    onError?: (error: string) => void;
};

/**
 * Connect to the SSE stream for the active account.
 * Only triggers notifications for NEW messages (signal events).
 */
export async function connectStream(handlers: StreamEventHandler = {}): Promise<void> {
    if (inboxStream) {
        inboxStream.disconnect();
    }

    const account = await getActiveAccount();
    if (!account) {
        await setState({ status: 'setup_required' });
        return;
    }

    const { deviceKey, email } = account;

    // Create a client with the device credentials
    const client = createScopedClient(deviceKey);
    inboxStream = new InboxStream(client);

    // Wire up handlers
    inboxStream.onStatusChange = async (status: 'connecting' | 'connected' | 'disconnected') => {
        await setState({ connectionStatus: status });
        handlers.onConnectionChange?.(status);
        if (status === 'connected') {
            await markAccountKeyStatus(email, 'valid');
            logger.info('Stream connected', { email });
        }
    };

    inboxStream.onSnapshot = async (messages: any[]) => {
        // Map types if necessary, though shared types should match
        const processed: Message[] = [];
        for (const raw of messages) {
            processed.push(await processMessagePayload(raw, account));
        }
        logger.info('Stream snapshot received', { email, count: processed.length });
        await updateMessages(email, processed);
        handlers.onSnapshot?.(processed);
    };

    inboxStream.onMessage = async (rawMessage: any) => {
        const message = await processMessagePayload(rawMessage, account);
        logger.info('Stream signal received', { email, subject: message.subject });

        const isNew = await addMessage(email, message);
        if (isNew) {
            handlers.onSignal?.(message);
            showNotification(message);
        }
    };

    // Start connection
    await inboxStream.connect();
}

/**
 * Show a native notification for a new message.
 */
function showNotification(message: Message): void {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    // Don't notify if document is visible and focused
    if (document.visibilityState === 'visible' && document.hasFocus()) {
        return;
    }

    new Notification(`📬 ${message.sender.split('@')[0]}`, {
        body: message.subject || '(No subject)',
        icon: '/pwa-192x192.png',
        tag: message.id, // Dedup
    });
}

/**
 * Disconnect the stream.
 */
export function disconnectStream(): void {
    if (inboxStream) {
        inboxStream.disconnect();
        inboxStream = null;
    }
}

async function processMessagePayload(rawMsg: any, account: Account): Promise<Message> {
    if (rawMsg.meta_ct || rawMsg.meta_v) {
        // V7 Encrypted Payload
        // Default: Persistent Placeholder
        let subject = 'Signal received';
        let preview: string | undefined;
        let sender = 'Signal'; // Minimal metadata
        let decrypted = false;

        if (account?.mailboxKey && rawMsg.meta_ct) {
            try {
                // Decode Base64 key
                const binStr = atob(account.mailboxKey);
                const keyBytes = new Uint8Array(binStr.length);
                for (let i = 0; i < binStr.length; i++) keyBytes[i] = binStr.charCodeAt(i);

                const decryptedData = await decryptMessagePreview(rawMsg.meta_ct, keyBytes);
                subject = decryptedData.subject;
                preview = decryptedData.preview;
                sender = 'Sisumail'; // Or extract from decrypted if added later. Current mock uses fallback.
                decrypted = true;
            } catch (_e) {
                // Decryption failed or keys invalid. Keep placeholder.
            }
        }

        const message: Message = {
            id: rawMsg.id,
            sender: sender,
            subject: subject,
            received_at: rawMsg.received_at || new Date().toISOString(),
            decrypted: decrypted,
        };

        if (preview) {
            message.preview = preview;
        }

        return message;
    } else {
        throw new Error('Unsupported legacy message format');
    }
}

/**
 * Claim a pairing token from a QR code.
 * Returns the device credentials if successful.
 */
export async function claimPairingToken(
    token: string,
    label?: string
): Promise<{
    success: boolean;
    email?: string;
    deviceKey?: { id: string; secret: string };
    error?: string;
}> {
    try {
        // Use centralized api (anonymous) or scoped if needed
        // claimPairing usually doesn't need auth, so anonymous 'api' is fine.
        // BUT wait, 'api' is SisumailClient instance, does it have 'devices'?
        // The centralized 'api' from lib/api.ts is a SisumailClient.
        const { data } = (await api.devices.claimPairing({
            token,
            label: label || 'Mobile App',
        } as any)) as any;

        const deviceCode = data.device_code;
        let id = '';
        let secret = '';

        // Canonical format: <kid>:<secret>
        const colonIndex = deviceCode.indexOf(':');
        if (colonIndex !== -1) {
            id = deviceCode.substring(0, colonIndex);
            secret = deviceCode.substring(colonIndex + 1);
        }

        if (!id || !secret) {
            return { success: false, error: 'Invalid device code format' };
        }

        return {
            success: true,
            email: data.email,
            deviceKey: { id, secret },
        };
    } catch (e: any) {
        return { success: false, error: e.message || 'Pairing failed' };
    }
}

// =============================================================================
// Smart Pairing (No-QR Flow)
// =============================================================================

interface PendingPairing {
    request_id: string;
    request_secret: string;
    identityId: string;
    privateKey: CryptoKey; // Store the CryptoKey instance directly
    createdAt: number;
}

let pendingPairing: PendingPairing | null = null;

/**
 * Request pairing approval from trusted device.
 * Generates ECDH keypair and sends request to API.
 */
export async function requestPairing(
    identityId: string,
    label: string = 'Mobile App'
): Promise<{ success: boolean; request_id?: string; error?: string }> {
    try {
        const keyPair = await generatePairingKeys();
        const publicKeyJwk = keyPair.publicKey;
        const client_fingerprint = await getDeviceFingerprint();

        // Use centralized api
        const { data } = await api.devices.requestPairing({
            identity_id: identityId.trim(),
            label,
            client_type: 'mobile',
            client_fingerprint,
            device_pubkey_jwk: JSON.stringify(publicKeyJwk),
        });

        const { request_id, request_secret } = data;

        pendingPairing = {
            request_id,
            request_secret,
            identityId: identityId.trim(),
            privateKey: keyPair.privateKey,
            createdAt: Date.now(),
        };

        return { success: true, request_id };
    } catch (e: any) {
        return { success: false, error: e.message || 'Request failed' };
    }
}

/**
 * Poll for pairing approval status.
 */
export async function pollPairing(): Promise<{
    success: boolean;
    status?: 'pending' | 'approved' | 'rejected' | 'expired';
    identityId?: string;
    deviceKey?: { id: string; secret: string };
    mailboxKey?: string;
    retry_after?: number;
    error?: string;
}> {
    if (!pendingPairing) {
        return { success: false, error: 'No pending pairing request' };
    }

    try {
        // Use centralized api
        // Polling uses the same claim endpoint but with request_id/secret
        const { data } = await api.devices.claimPairing({
            request_id: pendingPairing.request_id,
            request_secret: pendingPairing.request_secret,
        });

        const result = data;

        // Status: Pending
        if (result.status === 'pending') {
            return {
                success: true,
                status: 'pending',
                retry_after: result.retry_after || 3,
            };
        }

        if (result.status === 'approved' && result.device_key) {
            const identityId = pendingPairing.identityId;

            // Recover Mailbox Key if present
            let mailboxKeyB64: string | undefined;
            if (result.encrypted_blob) {
                try {
                    // Parse the encrypted_blob JSON
                    const envelope =
                        typeof result.encrypted_blob === 'string'
                            ? JSON.parse(result.encrypted_blob)
                            : result.encrypted_blob;

                    const recoveredKey = await recoverMailboxKey(
                        envelope,
                        pendingPairing.privateKey,
                        identityId,
                        pendingPairing.request_id
                    );

                    // Convert to Base64 (using helper to ensure consistency)
                    mailboxKeyB64 = encodeBase64Url(recoveredKey);
                } catch (_e) {
                    // We continue, but app won't be able to decrypt past messages
                }
            }

            pendingPairing = null;
            const successResponse: any = {
                success: true,
                status: 'approved',
                identityId,
                deviceKey: result.device_key,
            };
            if (mailboxKeyB64) {
                successResponse.mailboxKey = mailboxKeyB64;
            }
            return successResponse;
        }

        if (result.status === 'rejected' || result.status === 'expired') {
            pendingPairing = null;
            return { success: true, status: result.status };
        }

        // Fallback
        return {
            success: true,
            status: 'pending',
            retry_after: 3,
        };
    } catch (e: any) {
        return { success: false, error: e.message || 'Network error' };
    }
}

/**
 * Cancel pending pairing request.
 */
export function cancelPairing(): void {
    pendingPairing = null;
}

/**
 * Get pending identity (for UI display).
 */
export function getPendingIdentity(): string | null {
    return pendingPairing?.identityId || null;
}
